export type CartCoordinatorSnapshot = {
  addingAll: boolean;
  pending: string[];
};

export type CartExecution<T = any> = {
  id: string;
  item: any;
  result?: T;
  error?: unknown;
  skipped?: boolean;
};

export function createWishlistCartCoordinator(
  onChange: (snapshot: CartCoordinatorSnapshot) => void = () => undefined
) {
  let addingAll = false;
  const pending = new Set<string>();
  const publish = () => onChange({ addingAll, pending: [...pending] });

  async function runOne<T>(
    item: any,
    operation: (item: any) => Promise<T>
  ): Promise<CartExecution<T>> {
    const id = String(item.id);
    if (addingAll || pending.has(id)) return { id, item, skipped: true };
    pending.add(id);
    publish();
    try {
      return { id, item, result: await operation(item) };
    } catch (error) {
      return { id, item, error };
    } finally {
      pending.delete(id);
      publish();
    }
  }

  async function runAll<T>(
    items: any[],
    operation: (item: any) => Promise<T>
  ): Promise<Array<CartExecution<T>>> {
    if (addingAll || pending.size > 0) return [];
    addingAll = true;
    publish();
    const executions: Array<CartExecution<T>> = [];
    try {
      for (const item of items) {
        const id = String(item.id);
        pending.add(id);
        publish();
        try {
          executions.push({ id, item, result: await operation(item) });
        } catch (error) {
          executions.push({ id, item, error });
        } finally {
          pending.delete(id);
          publish();
        }
      }
      return executions;
    } finally {
      pending.clear();
      addingAll = false;
      publish();
    }
  }

  return { runOne, runAll };
}
